/**
 * PropertyElectionBand — Step C human election of the property L_PROP payable.
 *
 * The accountant picks a SIDE per matched pair (or a whole-side bulk choice); the deterministic
 * property engine computes L_PROP. NO dollar is ever entered here — only a side. Every figure shown
 * is verbatim from a stored line (per-pair extended_price) or from the engine-computed PATCH
 * response (elected payable / per-side totals); this component NEVER sums money client-side.
 *
 * Fixture/clone only: renders nothing unless the reconciliation is synthetic (the backend PATCH is
 * fixture-guarded — data.property_loss is a live engine input on real cases). Read-only for money.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FinancialValue } from '../shared/FinancialValue';
import {
  patchPropertyElection,
  getPropertyElection,
  getElectionRecommendation,
  type PropertyReconciliation,
  type MatchDetail,
  type ElectionSide,
  type ElectionResult,
  type ElectionRecommendationItem,
} from '../../services/propertyReconciliationApi';

// Human-readable label for a review-routing basis + its resolving action (advisory hint only).
const BASIS_HINT: Record<string, { label: string; action: string }> = {
  uncertain_pairing: { label: 'confirm pairing', action: 'is this the same line?' },
  undecidable_units: { label: 'unit basis', action: 'units unknown — price vs qty cannot be split' },
  quantity_dispute: { label: 'measure', action: 'quantities differ' },
  price_dispute: { label: 'pricing support', action: 'scope agrees, price differs' },
  no_dispute: { label: '', action: '' },
};

interface Props {
  reconciliation: PropertyReconciliation | null;
  seat?: 'accountant' | 'adjuster';
}

// A choosable two-sided pair, projected from the persisted reconciliation the workbench renders.
interface PairView {
  matchId: string;
  label: string;
  carrierPrice: string; // verbatim a_lines extended_price (carrier = adjuster side)
  expertPrice: string; // verbatim b_lines extended_price (expert = contractor side)
}

function isTwoSided(m: MatchDetail): boolean {
  return (m.a_lines?.length ?? 0) > 0 && (m.b_lines?.length ?? 0) > 0 && !!m.match_id;
}

// Sum of a side's verbatim extended_price strings, for the row label only when a pair bundles many
// lines. Kept as a display convenience on ALREADY-STORED numbers — not an elected total (that is
// engine-authoritative and only ever read from the PATCH response).
function sideLabelPrice(lines: MatchDetail['a_lines']): string {
  if (!lines || lines.length === 0) return '—';
  if (lines.length === 1) return lines[0].extended_price ?? '—';
  return `${lines.length} lines`;
}

const SEG_BASE =
  'px-3 h-8 text-[11px] uppercase tracking-[0.06em] font-mono border transition-colors';
// The ONE brand orange (law token) for the active/selected action — sparingly, as an outline.
const SEG_ON =
  'border-[var(--color-brand)] text-[var(--color-brand)] bg-[var(--color-brand)]/[0.08]';
const SEG_OFF =
  'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]';

const PropertyElectionBand: React.FC<Props> = ({ reconciliation, seat = 'accountant' }) => {
  const reportId = reconciliation?.report_id ?? '';
  const synthetic = reconciliation?._meta?.synthetic === true;
  const inputHash = reconciliation?._meta?.input_hash ?? '';
  const runAt = reconciliation?._meta?.run_at ?? '';

  const pairs: PairView[] = useMemo(() => {
    const md = reconciliation?.matches_detail ?? [];
    return md.filter(isTwoSided).map((m) => ({
      matchId: m.match_id as string,
      label:
        m.a_lines[0]?.description || m.b_lines[0]?.description || (m.match_id as string),
      carrierPrice: sideLabelPrice(m.a_lines),
      expertPrice: sideLabelPrice(m.b_lines),
    }));
  }, [reconciliation]);

  const [mode, setMode] = useState<'adjuster' | 'contractor' | 'mixed' | null>(null);
  const [picks, setPicks] = useState<Record<string, ElectionSide>>({});
  const [result, setResult] = useState<ElectionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  // Step-B advisory hints, keyed by match_id. DISPLAY-ONLY — they never seed `picks`; the human's
  // clicks are the only thing that fills the toggles, so `allPicked` counts only human decisions.
  const [recs, setRecs] = useState<Record<string, ElectionRecommendationItem>>({});
  // A non-404 recommendation error is a STRUCTURAL DEFECT the backend deliberately surfaces (e.g. a
  // 422 for a dangling variance→match reference). Show it as a muted note instead of silently hiding
  // it — the election still works, but the human should know the hints are missing and why.
  const [recsNote, setRecsNote] = useState<string | null>(null);

  // Hydrate the current election on load so the band reflects the persisted decision.
  useEffect(() => {
    let live = true;
    if (!reportId || (seat !== 'adjuster' && !synthetic)) return;
    getPropertyElection(reportId)
      .then((rec) => {
        if (!live || !rec) return;
        setStale(Boolean(rec.stale));
        if (rec.choice === 'contractor' || rec.choice === 'adjuster' || rec.choice === 'mixed') {
          setMode(rec.choice);
        }
        if (rec.line_elections) setPicks(rec.line_elections);
      })
      .catch(() => {/* no prior election — leave the band unset */});
    // Advisory review-routing hints (read-only; the recommendation never elects — the human does).
    getElectionRecommendation(reportId)
      .then((r) => {
        if (!live) return;
        setRecsNote(null);
        if (!r) return;  // 404 — no reconciliation yet; picker works without hints
        const byId: Record<string, ElectionRecommendationItem> = {};
        for (const item of r.recommendations) byId[item.match_id] = item;
        setRecs(byId);
      })
      .catch((err: any) => {
        if (!live) return;
        // 404 already resolves to null above; any other status is a structural defect worth surfacing.
        const status = err?.status ?? err?.response?.status;
        setRecsNote(
          status === 422
            ? 'Review-routing hints unavailable: the reconciliation has a structural defect (a variance references a non-paired match).'
            : 'Review-routing hints unavailable.',
        );
      });
    return () => {
      live = false;
    };
  }, [synthetic, reportId]);

  const gatedOut = seat === 'adjuster' ? pairs.length === 0 : (!synthetic || pairs.length === 0);
  if (gatedOut) return null;

  const allPicked = pairs.every((p) => picks[p.matchId]);
  const canApply =
    !busy && (mode === 'adjuster' || mode === 'contractor' || (mode === 'mixed' && allPicked));

  const apply = async () => {
    if (!mode || !canApply) return;
    setBusy(true);
    setError(null);
    try {
      const res = await patchPropertyElection(reportId, {
        choice: mode,
        input_hash: inputHash,
        run_at: runAt,
        ...(mode === 'mixed' ? { line_elections: picks } : {}),
      });
      setResult(res);
      setStale(false);
    } catch (e: any) {
      const status = e?.status ?? e?.response?.status;
      const detail = e?.body?.detail ?? e?.response?.data?.detail ?? '';
      if (status === 409) {
        setError('The reconciliation changed since you loaded it — refresh and re-elect.');
        setStale(true);
      } else {
        setError(detail || 'Could not record the election.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
            property payable election
          </span>
          {synthetic && (
            <span className="text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)] font-mono">
              fixture
            </span>
          )}
        </div>
        <span className="text-[10px] text-[var(--color-text-muted)]">
          You pick the side; the property engine computes the payable amount. No dollar is entered.
        </span>
      </div>

      {stale && (
        <div className="mt-2 text-[11px] text-[var(--color-brand)] font-mono">
          This election predates the current reconciliation — re-elect to refresh the payable.
        </div>
      )}

      {/* mode selector */}
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <button type="button" onClick={() => setMode('adjuster')}
          className={`${SEG_BASE} ${mode === 'adjuster' ? SEG_ON : SEG_OFF}`}>
          Adjuster · carrier
        </button>
        <button type="button" onClick={() => setMode('contractor')}
          className={`${SEG_BASE} ${mode === 'contractor' ? SEG_ON : SEG_OFF}`}>
          Contractor · expert
        </button>
        <button type="button" onClick={() => setMode('mixed')}
          className={`${SEG_BASE} ${mode === 'mixed' ? SEG_ON : SEG_OFF}`}>
          Mixed · pick per pair
        </button>
        <button type="button" onClick={apply} disabled={!canApply}
          className={`${SEG_BASE} ml-1 ${canApply
            ? SEG_ON
            : 'border-[var(--color-border)] text-[var(--color-text-muted)] cursor-not-allowed'}`}>
          {busy ? 'Applying…' : 'Apply election'}
        </button>
      </div>

      {/* mixed — per-pair side picker (verbatim per-side prices; no client total) */}
      {mode === 'mixed' && (
        <div className="mt-2 max-h-56 overflow-y-auto border border-[var(--color-border)]">
          {recsNote && (
            <div className="px-3 py-1.5 text-[10px] text-[var(--color-text-muted)] font-mono border-b border-[var(--color-border)]">
              {recsNote}
            </div>
          )}
          {pairs.map((p) => {
            const rec = recs[p.matchId];
            const hint = rec && rec.basis !== 'no_dispute' ? BASIS_HINT[rec.basis] : null;
            return (
            <div key={p.matchId}
              className="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-[var(--color-border)] last:border-b-0">
              <span className="min-w-0 flex items-center gap-2">
                <span className="text-[12px] text-[var(--color-text-secondary)] truncate">{p.label}</span>
                {hint && (
                  <span className="shrink-0 text-[9px] uppercase tracking-[0.06em] font-mono px-1 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)]"
                    title={`Review routing: ${hint.action} (engine cite ${rec.cite})`}>
                    {hint.label}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => setPicks((s) => ({ ...s, [p.matchId]: 'adjuster' }))}
                  className={`px-2 h-7 text-[11px] font-mono border ${picks[p.matchId] === 'adjuster' ? SEG_ON : SEG_OFF}`}>
                  Carrier <span className="tabular-nums">{p.carrierPrice}</span>
                </button>
                <button type="button" onClick={() => setPicks((s) => ({ ...s, [p.matchId]: 'contractor' }))}
                  className={`px-2 h-7 text-[11px] font-mono border ${picks[p.matchId] === 'contractor' ? SEG_ON : SEG_OFF}`}>
                  Expert <span className="tabular-nums">{p.expertPrice}</span>
                </button>
              </div>
            </div>
            );
          })}
          {!allPicked && (
            <div className="px-3 py-1.5 text-[10px] text-[var(--color-text-muted)] font-mono">
              Pick a side for every pair to apply a mixed election.
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 text-[11px] text-[var(--color-blocker)] font-mono">{error}</div>
      )}

      {/* elected result — every figure engine-computed / read from the response, never client math */}
      {result && (
        <div className="mt-3 border-t border-[var(--color-border)] pt-3">
          <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
                elected L_PROP payable · {result.choice}
              </div>
              <FinancialValue value={result.l_prop_payable} size="xl" cents />
              <div className="text-[10px] text-[var(--color-text-muted)] font-mono mt-0.5">
                {result.elected_line_count} lines · basis {result.l_prop_basis ?? 'RCV'}
                {result.acv_basis === 'absent' && ' · ACV —'}
              </div>
            </div>
            <div className="text-[11px] font-mono text-[var(--color-text-secondary)]">
              <div className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">side spread</div>
              <div>adjuster <FinancialValue value={result.per_side_totals.adjuster} className="text-[12px]" cents compact /></div>
              <div>contractor <FinancialValue value={result.per_side_totals.contractor} className="text-[12px]" cents compact /></div>
            </div>
            <div className="text-[11px] font-mono text-[var(--color-text-secondary)]">
              <div className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">one-sided (additive)</div>
              <div>+{result.unmatched_included.adjuster.n} carrier <FinancialValue value={result.unmatched_included.adjuster.total} className="text-[12px]" cents compact /></div>
              <div>+{result.unmatched_included.contractor.n} expert <FinancialValue value={result.unmatched_included.contractor.total} className="text-[12px]" cents compact /></div>
            </div>
          </div>

          {/* Coverage derivation (ISO CP 00 10 §D) — shown when a limit or deductible actually applied, so
              a capped/reduced payable is never a bare number without its gross → net basis. */}
          {result.coverage && (result.coverage.deductible_basis !== 'none' || result.coverage.limit_basis !== 'none') && (
            <div className="mt-2 pt-2 border-t border-[var(--color-border)] text-[11px] font-mono text-[var(--color-text-secondary)] flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">ISO CP 00 10 §D</span>
              <span>gross <FinancialValue value={result.coverage.gross} className="text-[12px]" cents compact /></span>
              {result.coverage.deductible_basis !== 'none' && (
                <span className="text-[var(--color-text-muted)]">− deductible <FinancialValue value={result.coverage.deductible_applied} className="text-[12px]" cents compact /></span>
              )}
              {result.coverage.limit_basis !== 'none' && (
                <span className={result.coverage.capped_at_limit ? 'text-[var(--color-brand)]' : 'text-[var(--color-text-muted)]'}>
                  {result.coverage.capped_at_limit ? '▸ capped at limit' : '↳ under limit'} <FinancialValue value={result.coverage.limit} className="text-[12px]" cents compact />
                </span>
              )}
              <span className="text-[var(--color-text-muted)]">= net</span>
              <FinancialValue value={result.coverage.net} className="text-[12px]" cents compact />
              {result.coverage.floored_at_zero && <span className="text-[var(--color-brand)]">· floored at $0</span>}
            </div>
          )}
          {result.coverage?.notes?.length ? (
            <div className="mt-1 text-[10px] text-[var(--color-text-muted)] font-mono">{result.coverage.notes.join(' · ')}</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default PropertyElectionBand;
